import type { LucideIcon } from "lucide-react";
import { Download, Eye, FilePenLine, Loader2, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar, type UserAvatarUser } from "@/components/ui/user-avatar";
import type { DocumentHistoryEntry } from "@/domains/documents/history/documentHistory";
import {
  documentHistoryDisplayName,
  documentHistoryStorageLabel,
  formatDocumentHistoryExactTime,
  formatDocumentHistoryRelativeTime,
} from "@/domains/documents/history/documentHistoryPresentation";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";
import { cn } from "@/lib/utils";
import { formatFileSize } from "@/utils/formatFileSize";

type DocumentHistoryCopy = DocumentTranslations["log"]["history"];

interface EventBadgeStyle {
  icon: LucideIcon;
  className: string;
}

const EVENT_BADGES: Record<DocumentHistoryEntry["action"], EventBadgeStyle> = {
  added: { icon: Plus, className: "bg-emerald-600" },
  edited: { icon: Pencil, className: "bg-blue-600" },
  renamed: { icon: FilePenLine, className: "bg-violet-600" },
  deleted: { icon: Trash2, className: "bg-red-600" },
  restored: { icon: RotateCcw, className: "bg-emerald-600" },
};

export interface DocumentHistoryRowActions {
  /** Whether this entry's file is openable at all — false hides the eye and
   *  leaves the filename as plain text, so neither is a click that no-ops. */
  canOpen: (entry: DocumentHistoryEntry) => boolean;
  open: (entry: DocumentHistoryEntry) => void;
  download: (entry: DocumentHistoryEntry) => void;
  restore: (entry: DocumentHistoryEntry) => void;
  versionHistory: (entry: DocumentHistoryEntry) => void;
}

interface DocumentHistoryRowProps {
  entry: DocumentHistoryEntry;
  language: string;
  copy: DocumentHistoryCopy;
  pendingRestore: boolean;
  actions: DocumentHistoryRowActions;
}

function actorUser(entry: DocumentHistoryEntry, unknownUser: string): UserAvatarUser {
  const name = entry.actor.name?.trim();
  const [firstName, ...lastName] = name?.split(/\s+/) ?? [];
  return {
    id: entry.actor.id ?? 0,
    first_name: firstName || null,
    last_name: lastName.join(" ") || null,
    email: name || unknownUser,
    avatar_url: entry.actor.avatarUrl,
    color: entry.actor.color,
  };
}

function RestoreRowAction({
  entry,
  copy,
  pendingRestore,
  onRestore,
}: {
  entry: DocumentHistoryEntry;
  copy: DocumentHistoryCopy;
  pendingRestore: boolean;
  onRestore: DocumentHistoryRowActions["restore"];
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="control"
      disabled={pendingRestore}
      onClick={() => onRestore(entry)}
    >
      {pendingRestore ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RotateCcw />}
      {copy.restore}
    </Button>
  );
}

function PreviewAction(props: Pick<DocumentHistoryRowProps, "entry" | "copy" | "actions">) {
  if (!props.actions.canOpen(props.entry)) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:bg-neutral-200 hover:text-foreground"
          aria-label={props.copy.open}
          onClick={() => props.actions.open(props.entry)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" className="z-50">
        {props.copy.open}
      </TooltipContent>
    </Tooltip>
  );
}

function DownloadAction(props: Pick<DocumentHistoryRowProps, "entry" | "copy" | "actions">) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 flex-shrink-0 p-0 text-muted-foreground hover:bg-neutral-200 hover:text-foreground"
          aria-label={props.copy.download}
          onClick={() => props.actions.download(props.entry)}
        >
          <Download className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" className="z-50">
        {props.copy.download}
      </TooltipContent>
    </Tooltip>
  );
}

function DocumentVersion(props: Pick<DocumentHistoryRowProps, "entry" | "copy" | "actions">) {
  if (props.entry.version == null) return null;
  const hasHistory =
    !props.entry.isDeleted && props.entry.version > 1 && props.entry.versionGroup !== null;
  return (
    <div className="flex-none">
      <VersionBadge
        version={props.entry.version}
        hasHistory={hasHistory}
        onClick={hasHistory ? () => props.actions.versionHistory(props.entry) : undefined}
        ariaLabel={hasHistory ? props.copy.versionHistory : undefined}
      />
      <span className="sr-only">
        {props.copy.version.replace("{version}", String(props.entry.version))}
      </span>
    </div>
  );
}

function EventAvatar(props: Pick<DocumentHistoryRowProps, "entry" | "copy">) {
  const { entry, copy } = props;
  const badge = EVENT_BADGES[entry.action];
  const BadgeIcon = badge.icon;
  return (
    <div className="relative mt-0.5 h-9 w-9 flex-none">
      <UserAvatar user={actorUser(entry, copy.unknownUser)} size={36} />
      <span
        className={cn(
          "absolute -bottom-1 -right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white text-white",
          badge.className
        )}
        title={copy.eventTypes[entry.action]}
        aria-label={copy.eventTypes[entry.action]}
      >
        <BadgeIcon className="h-2.5 w-2.5" aria-hidden="true" />
      </span>
    </div>
  );
}

function ActorLine(props: Pick<DocumentHistoryRowProps, "entry" | "language" | "copy">) {
  const { entry, language, copy } = props;
  const actorName = entry.actor.name?.trim() || copy.unknownUser;
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm text-neutral-700">
      <p className="min-w-0 truncate">
        <span className="font-semibold text-neutral-900">{actorName}</span>{" "}
        {copy.verbs[entry.action]}
      </p>
      <time
        dateTime={entry.createdAt}
        title={formatDocumentHistoryExactTime(entry.createdAt, language)}
        className="flex-none text-xs font-medium tabular-nums text-neutral-600"
      >
        {formatDocumentHistoryRelativeTime(entry.createdAt, copy.relativeTime)}
      </time>
    </div>
  );
}

function ChangeSummary(props: Pick<DocumentHistoryRowProps, "entry" | "copy">) {
  const change = props.entry.change;
  if (!change) return null;
  const before = change.before.join(", ") || props.copy.changes.none;
  const after = change.after.join(", ") || props.copy.changes.none;
  const fullChange = `${props.copy.changes.before}: ${before} → ${props.copy.changes.after}: ${after}`;

  return (
    <div className="mt-1 min-w-0 text-xs text-muted-foreground">
      <span className="font-medium text-neutral-700">
        {props.copy.changes.fields[change.field]}
      </span>
      <p className="break-words" title={fullChange}>
        <span className="sr-only">{props.copy.changes.before}: </span>
        {before}
        <span className="px-1" aria-hidden="true">
          →
        </span>
        <span className="sr-only">{props.copy.changes.after}: </span>
        {after}
      </p>
    </div>
  );
}

function DocumentIdentity(props: {
  row: DocumentHistoryRowProps;
  displayName: string;
  storageLabel: string;
}) {
  const { entry, copy, actions, pendingRestore } = props.row;
  return (
    <div className="mt-2 flex min-w-0 items-center gap-3" title={props.storageLabel}>
      <FileTypeIcon filename={props.displayName} contentType={entry.contentType} />
      <div className="min-w-0 flex-1">
        {actions.canOpen(entry) ? (
          <button
            type="button"
            className="block max-w-full cursor-pointer truncate text-left text-sm font-medium hover:text-primary hover:underline"
            onClick={() => actions.open(entry)}
          >
            {props.displayName}
          </button>
        ) : (
          <p className="truncate text-sm font-medium">{props.displayName}</p>
        )}
        {entry.size == null ? null : (
          <p className="mt-1 text-xs text-muted-foreground">{formatFileSize(entry.size)}</p>
        )}
      </div>
      <DocumentVersion entry={entry} copy={copy} actions={actions} />
      {entry.isDeleted ? (
        <RestoreRowAction
          entry={entry}
          copy={copy}
          pendingRestore={pendingRestore}
          onRestore={actions.restore}
        />
      ) : (
        <div className="flex flex-none items-center gap-1">
          <PreviewAction entry={entry} copy={copy} actions={actions} />
          <DownloadAction entry={entry} copy={copy} actions={actions} />
        </div>
      )}
    </div>
  );
}

function RowContent(props: {
  row: DocumentHistoryRowProps;
  displayName: string;
  storageLabel: string;
}) {
  const { row } = props;
  return (
    <div className="flex gap-3 px-5 py-4">
      <EventAvatar entry={row.entry} copy={row.copy} />
      <div className="min-w-0 flex-1">
        <ActorLine entry={row.entry} language={row.language} copy={row.copy} />
        <ChangeSummary entry={row.entry} copy={row.copy} />
        <DocumentIdentity
          row={row}
          displayName={props.displayName}
          storageLabel={props.storageLabel}
        />
      </div>
    </div>
  );
}

export function DocumentHistoryRow(props: DocumentHistoryRowProps) {
  const { entry } = props;
  const displayName = documentHistoryDisplayName(entry);
  const storageLabel = documentHistoryStorageLabel(entry);

  return (
    <li className="border-b transition-colors last:border-b-0 focus-within:bg-[#f7f7f7] hover:bg-[#f7f7f7]">
      <RowContent row={props} displayName={displayName} storageLabel={storageLabel} />
    </li>
  );
}
